/**
 * Private feedback transports with receipt validation.
 *
 * - `submitCvFeedback`: CV help (L1b) "Needs work" details -> POST
 *   /api/feedback/onboarding-preview. Same rules as the design reference's
 *   `feedback-transport.js`: a chip or a note is required, the note is
 *   NUL-stripped/trimmed and <= 1,000 chars, the idempotency key is 1-128
 *   chars, and only allowlisted fields are sent (never the CV).
 * - `submitCancellationFeedback`: optional cancel-journey note/chip -> POST
 *   /api/feedback/cancellation. Fire-and-forget from the UI's point of view;
 *   delivery never gates cancellation.
 *
 * "Feedback sent" may only be shown after a receipt passes
 * `validateFeedbackReceipt`. Any other response throws.
 */
import type {
  APIResponse,
  CancellationFeedbackInput,
  CvFeedbackReason,
  FeedbackPlatform,
  FeedbackReceipt,
  OnboardingPreviewFeedbackInput,
} from '@tabbio/types';
import { api } from '../api';
import { isDetailFor } from './cancel-journey';

export const CV_FEEDBACK_REASONS: readonly CvFeedbackReason[] = [
  'details',
  'wording',
  'layout',
  'other',
];

export const FEEDBACK_NOTE_MAX_LENGTH = 1000;
export const IDEMPOTENCY_KEY_MAX_LENGTH = 128;

export class FeedbackValidationError extends Error {
  constructor(
    message: string,
    readonly code:
      | 'INVALID_PAYLOAD'
      | 'INVALID_VOTE'
      | 'INVALID_REASON'
      | 'INVALID_MESSAGE'
      | 'MESSAGE_TOO_LONG'
      | 'EMPTY_FEEDBACK'
      | 'INVALID_IDEMPOTENCY_KEY'
      | 'INVALID_DETAIL',
  ) {
    super(message);
    this.name = 'FeedbackValidationError';
  }
}

export class FeedbackReceiptError extends Error {
  constructor(message = 'Feedback service did not return a valid receipt.') {
    super(message);
    this.name = 'FeedbackReceiptError';
  }
}

export function cleanFeedbackNote(value: unknown) {
  if (value == null) return '';
  if (typeof value !== 'string') {
    throw new FeedbackValidationError('Feedback message must be text.', 'INVALID_MESSAGE');
  }
  return value.replace(/\u0000/g, '').trim();
}

function validateIdempotencyKey(value: unknown): string {
  if (
    typeof value !== 'string' ||
    !value.trim() ||
    value.length > IDEMPOTENCY_KEY_MAX_LENGTH
  ) {
    throw new FeedbackValidationError(
      'A nonempty idempotency key of up to 128 characters is required.',
      'INVALID_IDEMPOTENCY_KEY',
    );
  }
  return value;
}

export interface CvFeedbackRequest {
  vote: 'no';
  reason?: CvFeedbackReason | null;
  message?: string | null;
  locale: string;
  idempotencyKey: string;
}

export interface ValidatedCvFeedbackRequest {
  vote: 'no';
  reason: CvFeedbackReason | null;
  message: string;
  locale: 'en' | 'ar';
  idempotencyKey: string;
}

/** Throws `FeedbackValidationError`; Submit stays disabled until this passes. */
export function validateCvFeedbackRequest(
  payload: unknown,
): ValidatedCvFeedbackRequest {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new FeedbackValidationError('Feedback payload is required.', 'INVALID_PAYLOAD');
  }
  const input = payload as Record<string, unknown>;
  if (input.vote !== 'no') {
    throw new FeedbackValidationError('Issue feedback must use vote: no.', 'INVALID_VOTE');
  }
  const reason =
    input.reason == null || input.reason === '' ? null : input.reason;
  if (reason !== null && !CV_FEEDBACK_REASONS.includes(reason as CvFeedbackReason)) {
    throw new FeedbackValidationError('Unknown issue reason.', 'INVALID_REASON');
  }
  const message = cleanFeedbackNote(input.message);
  if (message.length > FEEDBACK_NOTE_MAX_LENGTH) {
    throw new FeedbackValidationError(
      'Feedback message must be 1,000 characters or fewer.',
      'MESSAGE_TOO_LONG',
    );
  }
  if (!reason && !message) {
    throw new FeedbackValidationError('Choose a reason or add a note.', 'EMPTY_FEEDBACK');
  }
  return Object.freeze({
    vote: 'no' as const,
    reason: reason as CvFeedbackReason | null,
    message,
    locale: input.locale === 'ar' ? ('ar' as const) : ('en' as const),
    idempotencyKey: validateIdempotencyKey(input.idempotencyKey),
  });
}

/** True when L1b's Submit may be enabled (a chip, or a non-blank note). */
export function canSubmitCvFeedback(
  reason: CvFeedbackReason | null | undefined,
  message: string | null | undefined,
) {
  try {
    return Boolean(reason || cleanFeedbackNote(message ?? ''));
  } catch {
    return false;
  }
}

/**
 * The only shape that permits "Feedback sent": status 'received', a nonempty
 * feedbackId and a parseable receivedAt. Extra fields are dropped.
 */
export function validateFeedbackReceipt(value: unknown): FeedbackReceipt {
  const receipt =
    value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  if (
    !receipt ||
    receipt.status !== 'received' ||
    typeof receipt.feedbackId !== 'string' ||
    !receipt.feedbackId.trim() ||
    typeof receipt.receivedAt !== 'string' ||
    !Number.isFinite(Date.parse(receipt.receivedAt))
  ) {
    throw new FeedbackReceiptError();
  }
  return Object.freeze({
    status: 'received' as const,
    feedbackId: receipt.feedbackId,
    receivedAt: receipt.receivedAt,
  });
}

export type FeedbackTransport = (
  path: string,
  body: unknown,
) => Promise<APIResponse<unknown>>;

const defaultTransport: FeedbackTransport = (path, body) =>
  api.post<unknown>(path, body);

function receiptFromEnvelope(response: APIResponse<unknown>) {
  if (response.error) {
    throw new FeedbackReceiptError(response.error.message);
  }
  return validateFeedbackReceipt(response.data);
}

/**
 * L1b Submit. `accountId` must be the signed-in account (the server rejects a
 * mismatch). Reuse the same `idempotencyKey` for a retry of unchanged content;
 * mint a new one when the chip or note changes.
 */
export async function submitCvFeedback(
  request: CvFeedbackRequest & {
    accountId: string;
    platform: FeedbackPlatform;
    appVersion?: string;
  },
  options: { transport?: FeedbackTransport } = {},
): Promise<FeedbackReceipt> {
  const valid = validateCvFeedbackRequest(request);
  if (typeof request.accountId !== 'string' || !request.accountId) {
    throw new FeedbackValidationError('An account is required.', 'INVALID_PAYLOAD');
  }
  // Allowlist: never forward CVs, identity beyond the account check, or
  // arbitrary caller metadata.
  const body: OnboardingPreviewFeedbackInput = {
    accountId: request.accountId,
    sentiment: 'down',
    platform: request.platform,
    locale: valid.locale,
    ...(request.appVersion ? { appVersion: request.appVersion.slice(0, 60) } : {}),
    ...(valid.reason ? { reason: valid.reason } : {}),
    ...(valid.message ? { message: valid.message } : {}),
    idempotencyKey: valid.idempotencyKey,
  };
  const transport = options.transport ?? defaultTransport;
  return receiptFromEnvelope(
    await transport('/api/feedback/onboarding-preview', body),
  );
}

/** Validates, then POSTs /api/feedback/cancellation. Throws on any failure. */
export async function submitCancellationFeedback(
  payload: CancellationFeedbackInput,
  options: { transport?: FeedbackTransport } = {},
): Promise<FeedbackReceipt> {
  if (!payload || typeof payload !== 'object') {
    throw new FeedbackValidationError('Feedback payload is required.', 'INVALID_PAYLOAD');
  }
  const detail = payload.detail ?? null;
  if (detail !== null && !isDetailFor(payload.reason, detail)) {
    throw new FeedbackValidationError('Unknown detail for this reason.', 'INVALID_DETAIL');
  }
  const message = cleanFeedbackNote(payload.message);
  if (message.length > FEEDBACK_NOTE_MAX_LENGTH) {
    throw new FeedbackValidationError(
      'Feedback message must be 1,000 characters or fewer.',
      'MESSAGE_TOO_LONG',
    );
  }
  if (!detail && !message) {
    throw new FeedbackValidationError('Choose a detail or add a note.', 'EMPTY_FEEDBACK');
  }
  const body: CancellationFeedbackInput = {
    reason: payload.reason,
    detail,
    message: message || null,
    locale: payload.locale === 'ar' ? 'ar' : 'en',
    idempotencyKey: validateIdempotencyKey(payload.idempotencyKey),
    planSet: payload.planSet,
    tier: payload.tier,
    ...(payload.platform ? { platform: payload.platform } : {}),
  };
  const transport = options.transport ?? defaultTransport;
  return receiptFromEnvelope(await transport('/api/feedback/cancellation', body));
}
